'use client';

import { useCallback, useEffect, useState } from 'react';
import { Mic, Volume2, RefreshCw, AlertCircle, Settings2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../core/select';
import { Button } from '../core/button';
import { Popover, PopoverContent, PopoverTrigger } from '../core/popover';
import { cn } from '../../utils';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

interface AudioSettingsProps {
  /** Currently selected microphone device ID */
  selectedMicId?: string;
  /** Callback when a microphone is selected */
  onMicSelect: (deviceId: string) => void;
  /** Currently selected speaker device ID */
  selectedSpeakerId?: string;
  /** Callback when a speaker is selected */
  onSpeakerSelect?: (deviceId: string) => void;
  /** Whether to show speaker selection (some browsers don't support it) */
  showSpeakerSelection?: boolean;
  /** Optional className for the container */
  className?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
}

interface AudioSettingsPopoverProps extends AudioSettingsProps {
  /** Whether the popover trigger is disabled */
  triggerDisabled?: boolean;
}

/**
 * AudioSettings component allows users to select their preferred microphone
 * and speaker from available audio devices.
 */
export function AudioSettings({
  selectedMicId,
  onMicSelect,
  selectedSpeakerId,
  onSpeakerSelect,
  showSpeakerSelection = true,
  className,
  disabled = false,
}: AudioSettingsProps) {
  const [microphones, setMicrophones] = useState<AudioDevice[]>([]);
  const [speakers, setSpeakers] = useState<AudioDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [speakerSupported, setSpeakerSupported] = useState(true);

  const loadDevices = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices?.enumerateDevices) {
        setError('Your browser does not support device selection');
        setIsLoading(false);
        return;
      }

      // First, try to enumerate devices without requesting permission
      let devices = await navigator.mediaDevices.enumerateDevices();
      let audioInputs = devices.filter((d) => d.kind === 'audioinput');
      let audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

      // If we don't have labels, we need to request permission first
      const needsPermission = audioInputs.length === 0 || audioInputs.every((d) => !d.label);

      if (needsPermission) {
        try {
          // Request microphone permission to get device labels
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Stop the stream immediately - we just needed it for permission
          stream.getTracks().forEach((track) => track.stop());

          // Re-enumerate devices now that we have permission
          devices = await navigator.mediaDevices.enumerateDevices();
          audioInputs = devices.filter((d) => d.kind === 'audioinput');
          audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
        } catch (permError) {
          // Still show devices without labels if available
          if (audioInputs.length === 0) {
            setError('Microphone access denied. Please allow microphone access to select devices.');
            setIsLoading(false);
            return;
          }
        }
      }

      // Map microphones
      const mappedMics: AudioDevice[] = audioInputs.map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${index + 1}`,
      }));
      setMicrophones(mappedMics);

      // Auto-select first mic if none selected or selected doesn't exist
      if (mappedMics.length > 0) {
        const selectedExists = selectedMicId && mappedMics.some((d) => d.deviceId === selectedMicId);
        if (!selectedExists) {
          onMicSelect(mappedMics[0].deviceId);
        }
      }

      // Map speakers (may not be available in all browsers)
      if (audioOutputs.length > 0) {
        const mappedSpeakers: AudioDevice[] = audioOutputs.map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${index + 1}`,
        }));
        setSpeakers(mappedSpeakers);
        setSpeakerSupported(true);

        // Auto-select first speaker if none selected or selected doesn't exist
        if (mappedSpeakers.length > 0 && onSpeakerSelect) {
          const selectedExists = selectedSpeakerId && mappedSpeakers.some((d) => d.deviceId === selectedSpeakerId);
          if (!selectedExists) {
            onSpeakerSelect(mappedSpeakers[0].deviceId);
          }
        }
      } else {
        setSpeakerSupported(false);
      }
    } catch (err) {
      console.error('Failed to load audio devices:', err);
      setError('Failed to load audio devices');
    } finally {
      setIsLoading(false);
    }
  }, [selectedMicId, onMicSelect, selectedSpeakerId, onSpeakerSelect]);

  // Load devices on mount
  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Listen for device changes (e.g., plugging in a new device)
  useEffect(() => {
    const handleDeviceChange = () => {
      loadDevices();
    };

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [loadDevices]);

  const handleRefresh = () => {
    loadDevices();
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Audio Settings</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading}
          className="h-7 px-2 text-xs text-gray-500 hover:text-[#cf2e2e] hover:bg-rose-100"
          title="Refresh device list"
        >
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600">{error}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            className="ml-auto h-6 px-2 text-xs"
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Microphone Selection */}
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#cf2e2e] to-[#f78da7] shadow-sm flex-shrink-0">
              <Mic className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-xs text-gray-500 mb-1 block">Microphone</label>
              <Select
                value={selectedMicId}
                onValueChange={onMicSelect}
                disabled={disabled || isLoading || microphones.length === 0}
              >
                <SelectTrigger className="h-8 text-xs border-gray-200 bg-white hover:bg-gray-50">
                  <SelectValue
                    placeholder={
                      isLoading
                        ? 'Loading...'
                        : microphones.length === 0
                          ? 'No microphones found'
                          : 'Select microphone'
                    }
                  />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  {microphones.map((device) => (
                    <SelectItem key={device.deviceId} value={device.deviceId} className="text-xs">
                      {device.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Speaker Selection */}
          {showSpeakerSelection && speakerSupported && speakers.length > 0 && onSpeakerSelect && (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#6366f1] to-[#a5b4fc] shadow-sm flex-shrink-0">
                <Volume2 className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <label className="text-xs text-gray-500 mb-1 block">Speaker</label>
                <Select
                  value={selectedSpeakerId}
                  onValueChange={onSpeakerSelect}
                  disabled={disabled || isLoading || speakers.length === 0}
                >
                  <SelectTrigger className="h-8 text-xs border-gray-200 bg-white hover:bg-gray-50">
                    <SelectValue
                      placeholder={
                        isLoading
                          ? 'Loading...'
                          : speakers.length === 0
                            ? 'No speakers found'
                            : 'Select speaker'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    {speakers.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId} className="text-xs">
                        {device.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Note about speaker selection not being supported */}
          {showSpeakerSelection && !speakerSupported && (
            <p className="text-xs text-gray-400 italic">
              Speaker selection is not supported in this browser.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AudioSettingsPopover - A popover trigger button with audio settings content.
 * Use this for placing audio settings in a corner of a component.
 */
export function AudioSettingsPopover({
  selectedMicId,
  onMicSelect,
  selectedSpeakerId,
  onSpeakerSelect,
  showSpeakerSelection = true,
  disabled = false,
  triggerDisabled = false,
}: AudioSettingsPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={triggerDisabled}
          className="h-8 px-2 text-gray-500 hover:text-[#cf2e2e] hover:bg-rose-50"
          title="Audio settings"
        >
          <Settings2 className="h-4 w-4" />
          <span className="ml-1.5 text-xs hidden sm:inline">Audio</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <AudioSettings
          selectedMicId={selectedMicId}
          onMicSelect={onMicSelect}
          selectedSpeakerId={selectedSpeakerId}
          onSpeakerSelect={onSpeakerSelect}
          showSpeakerSelection={showSpeakerSelection}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}

// Storage keys
const MIC_STORAGE_KEY = 'preferred-microphone-device-id';
const SPEAKER_STORAGE_KEY = 'preferred-speaker-device-id';

/**
 * Hook to manage audio device selection state with localStorage persistence.
 * Both microphone and speaker selections are remembered across sessions.
 */
export function useAudioSettings() {
  const [selectedMicId, setSelectedMicIdState] = useState<string | undefined>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(MIC_STORAGE_KEY) ?? undefined;
    }
    return undefined;
  });

  const [selectedSpeakerId, setSelectedSpeakerIdState] = useState<string | undefined>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(SPEAKER_STORAGE_KEY) ?? undefined;
    }
    return undefined;
  });

  // Wrapper that also persists mic to localStorage
  const setSelectedMicId = useCallback((deviceId: string | undefined) => {
    setSelectedMicIdState(deviceId);
    if (typeof window !== 'undefined') {
      if (deviceId) {
        localStorage.setItem(MIC_STORAGE_KEY, deviceId);
      } else {
        localStorage.removeItem(MIC_STORAGE_KEY);
      }
    }
  }, []);

  // Wrapper that also persists speaker to localStorage
  const setSelectedSpeakerId = useCallback((deviceId: string | undefined) => {
    setSelectedSpeakerIdState(deviceId);
    if (typeof window !== 'undefined') {
      if (deviceId) {
        localStorage.setItem(SPEAKER_STORAGE_KEY, deviceId);
      } else {
        localStorage.removeItem(SPEAKER_STORAGE_KEY);
      }
    }
  }, []);

  // Validate that stored devices still exist
  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const validateDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === 'audioinput');
        const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

        // Validate mic
        if (selectedMicId) {
          const micExists = audioInputs.some((d) => d.deviceId === selectedMicId);
          if (!micExists && audioInputs.length > 0) {
            setSelectedMicId(undefined);
          }
        }

        // Validate speaker
        if (selectedSpeakerId) {
          const speakerExists = audioOutputs.some((d) => d.deviceId === selectedSpeakerId);
          if (!speakerExists && audioOutputs.length > 0) {
            setSelectedSpeakerId(undefined);
          }
        }
      } catch {
        // Ignore errors during validation
      }
    };

    validateDevices();
  }, [selectedMicId, selectedSpeakerId, setSelectedMicId, setSelectedSpeakerId]);

  // Get constraints for getUserMedia with selected mic
  const getMicConstraints = useCallback(
    (additionalConstraints?: MediaTrackConstraints): MediaStreamConstraints => {
      return {
        audio: selectedMicId
          ? { deviceId: { exact: selectedMicId }, ...additionalConstraints }
          : additionalConstraints ?? true,
      };
    },
    [selectedMicId]
  );

  // Set sink ID on an audio element to use the selected speaker
  const setSpeakerOnElement = useCallback(
    async (audioElement: HTMLAudioElement | HTMLVideoElement): Promise<boolean> => {
      if (!selectedSpeakerId) return true;

      // Check if setSinkId is supported
      if (!('setSinkId' in audioElement)) {
        console.warn('setSinkId is not supported in this browser');
        return false;
      }

      try {
        await (audioElement as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(selectedSpeakerId);
        return true;
      } catch (err) {
        console.error('Failed to set audio output device:', err);
        return false;
      }
    },
    [selectedSpeakerId]
  );

  return {
    selectedMicId,
    setSelectedMicId,
    selectedSpeakerId,
    setSelectedSpeakerId,
    getMicConstraints,
    setSpeakerOnElement,
  };
}
